'use client';

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { Dialog, Typography, Button } from 'src/components';
import { useCreateTeam } from 'src/hooks/teams/useCreateTeam';
import { useUpdateTeam } from 'src/hooks/teams/useUpdateTeam';
import { UserMultiSelect } from '../UserMultiSelect';

interface Team {
  id: number;
  name: string;
  description?: string;
  users?: { id: string }[];
  is_full_company_team?: boolean;
}

interface CreateUpdateTeamModalProps {
  team?: Team | null; // If provided, we're updating; if null, we're creating
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateUpdateTeamModal: React.FC<CreateUpdateTeamModalProps> = ({
  team,
  open,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!team;
  const { createTeam, isCreating, error: createError } = useCreateTeam();
  const { updateTeam, isUpdating, error: updateError } = useUpdateTeam();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    users: [] as string[],
    id: '',
  });

  const [errors, setErrors] = useState({
    name: '',
  });

  // Initialize form data when editing or reset when modal closes
  useEffect(() => {
    if (team && open) {
      setFormData({
        name: team.name || '',
        description: team.description || '',
        users: team.users?.map((user) => user.id) || [],
        id: team.id.toString() || '',
      });
    } else {
      // Reset form when creating a new team or when modal closes
      setFormData({
        name: '',
        description: '',
        users: [],
        id: '',
      });
    }

    // Also reset errors when modal opens/closes
    setErrors({ name: '' });
  }, [team, open]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation errors when user types
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = { name: '' };
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditMode && team) {
        // Update existing team
        const result = await updateTeam(team.id.toString(), {
          id: team.id.toString(),
          ...formData,
        });
        if (result.success) {
          onClose();
          if (onSuccess) onSuccess();
        }
      } else {
        // Create new team
        const result = await createTeam(formData);
        if (result.success) {
          onClose();
          if (onSuccess) onSuccess();
        }
      }
    } catch (error) {
      console.error('Error saving team:', error);
    }
  };

  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  return (
    <Dialog isOpen={open} onClose={onClose} className="w-full max-w-md !h-144">
      <Dialog.Header>
        <div className="flex items-center gap-2 mb-2">
          <Icon icon={faUserGroup} className="text-neutral-700" />
          <Typography variant="h4" className="text-primary">
            {isEditMode ? 'Edit Team' : 'Create Team'}
          </Typography>
        </div>
      </Dialog.Header>

      <Dialog.Content>
        <div className="p-4">
          <div className="mb-4">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Team Name*
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border ${
                errors.name ? 'border-red-500' : 'border-neutral-300'
              } rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
              placeholder="Enter team name"
              data-testid="team-name-input"
            />
            {errors.name && (
              <p
                className="mt-1 text-sm text-red-600"
                data-testid="team-name-error"
              >
                {errors.name}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter team description"
              data-testid="team-description-input"
            />
          </div>

          {/* User selection component */}
          <div className="mb-4">
            <UserMultiSelect
              selectedUsers={formData.users}
              onChange={(users) => setFormData((prev) => ({ ...prev, users }))}
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <Typography className="text-sm text-red-600">
                {error.message || 'An error occurred while saving the team.'}
              </Typography>
            </div>
          )}
        </div>
      </Dialog.Content>

      <Dialog.Actions>
        <div className="flex justify-end gap-2 w-full">
          <Button
            variant="outlined"
            color="neutral"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSubmit}
            loading={isLoading}
            data-testid="save-team-button"
          >
            {isEditMode ? 'Save Changes' : 'Create Team'}
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default CreateUpdateTeamModal;
